import { Injectable, NgZone } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';

/**
 * Wykrywanie nowej wersji panelu (zamówienie Huberta 11.08).
 *
 * Panel to SPA: po wdrożeniu frontendu użytkownik pracuje na starych plikach
 * aż do przeładowania strony. W jednym dniu (11.08) rozbiło to trzy osoby:
 * Hubert nie widział proform w liście typów, Ania dostawała "nie udało się
 * utworzyć faktury" naprawione godzinę wcześniej, ikona opłacenia "nie
 * istniała". Każde takie zgłoszenie kosztuje diagnozę, która kończy się
 * na "wciśnij Ctrl+Shift+R".
 *
 * Mechanizm celowo BEZ zmian w backendzie i bez service workera: pobieramy
 * index.html (no-store, więc zawsze świeży) i porównujemy hash w nazwie
 * bundla `main.<hash>.js` z tym, który realnie działa w przeglądarce
 * (Angular wstawia content-hash do nazwy przy każdym buildzie). Różnica =
 * jest nowa wersja.
 *
 * Sprawdzamy co POLL_MS oraz przy każdym powrocie do karty (visibilitychange)
 * - to drugie łapie najczęstszy scenariusz: panel otwarty od rana, deploy
 * w południe, user wraca po przerwie.
 */
@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  private static readonly POLL_MS = 5 * 60 * 1000;

  private currentHash: string | null = null;
  private notified = false;

  constructor(
    private readonly snackBar: MatSnackBar,
    private readonly translate: TranslateService,
    private readonly zone: NgZone,
  ) {}

  start(): void {
    this.currentHash = this.readRunningHash();
    if (!this.currentHash) {
      // Tryb deweloperski (ng serve) nie hashuje bundli - nie ma czego pilnować.
      return;
    }

    // Poza strefą Angulara, żeby cykliczny timer nie budził change detection.
    this.zone.runOutsideAngular(() => {
      setInterval(() => this.check(), VersionCheckService.POLL_MS);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.check();
        }
      });
    });
  }

  private readRunningHash(): string | null {
    const script = document.querySelector<HTMLScriptElement>('script[src*="main."]');
    const match = script?.src?.match(/main\.([a-f0-9]{8,})\.js/);
    return match?.[1] ?? null;
  }

  private async check(): Promise<void> {
    if (this.notified) {
      return;
    }

    let html = '';
    try {
      const res = await fetch('index.html', { cache: 'no-store' });
      if (!res.ok) {
        return;
      }
      html = await res.text();
    } catch {
      // Chwilowy brak sieci nie jest powodem do niczego - sprawdzimy następnym razem.
      return;
    }

    const match = html.match(/main\.([a-f0-9]{8,})\.js/);
    if (!match || match[1] === this.currentHash) {
      return;
    }

    this.notified = true;
    this.zone.run(() => {
      const ref = this.snackBar.open(
        this.translate.instant('COMMON.NEW_VERSION_AVAILABLE'),
        this.translate.instant('COMMON.NEW_VERSION_RELOAD'),
        // Bez duration - komunikat wisi, aż user kliknie albo sam odświeży.
        // Znikający po 3 s pasek nikogo nie uratuje przed starą wersją.
        { panelClass: ['version-snackbar'] },
      );
      ref.onAction().subscribe(() => window.location.reload());
    });
  }
}
